<script context="module" lang="ts">
	export const lowStockChecker = (
		i: CartItem[] | CartItem,
	): { hasLowStock: boolean; amt: number } => {
		return {
			hasLowStock: Array.isArray(i)
				? i.filter((i) => i.low_stock === true).length > 0
				: i.low_stock === true,
			amt: Array.isArray(i) ? i.filter((i) => i.low_stock === true).length : 0,
		};
	};
</script>
<script lang="ts">
	import type { ProductData } from "$lib/def/landing";
	import type { Tooltip } from "proto/groceries_order";

	import { goto } from "$app/navigation";
	import { Vertical } from "$lib/def/verticals";
	import { profile$ } from "$lib/serv/core/auth";
	import { isFreeDelivery } from "$lib/serv/food-cart";
	import {
	    DEFAULT_MERCHANT_KEY,
	    checkout,
	    clearCart,
	    getLocalCartStream,
	    observeUpdateQtyRequests,
	    updateCartItemQuantity,
	    type CartData,
	    type CartItem
	} from "$lib/serv/groc-cart";
	import { toastError } from "$lib/serv/toast";
	import { extractErrorMessage, formatCurrency } from "$lib/util/format";
	import {
	    Subject,
	    Subscription,
	    combineLatest,
	    firstValueFrom,
	    map,
	    skip,
	    startWith,
	    tap,
	    type ObservedValueOf
	} from "rxjs";
	import { createEventDispatcher } from "svelte";
	import { setItem } from "$lib/serv/core/local-store";
	
	import SignIn from "$lib/ui/profile/SignIn.svelte";
	import Cart from "$lib/ui/shared/Cart.svelte";
	import CartNotifications from "$lib/ui/shared/CartNotifications.svelte";
	import LazyBackgroundImage from "$lib/ui/shared/LazyBackgroundImage.svelte";
	import LazyImage from "$lib/ui/shared/LazyImage.svelte";
	import Modal from "$lib/ui/shared/Modal.svelte";
	import Quantity from "$lib/ui/shared/Quantity.svelte";
	import ToolTip, { ToolPosition } from "$lib/ui/shared/ToolTip.svelte";
	import PnPLogo from "$lib/ui/shared/icons/PnPLogo.svelte";
	import BackupSelect from "./pdp/BackupSelect.svelte";
import { PNP_IMG_HOST } from "$lib/serv/core/env";

	export let expanded = false;

	// export let ref: CartRef;

	let showOutOfStockNotice = false;
	let showLogin = false;
	let showOvernightNotice = false;
	let loading = false;
	

	let dispatch = createEventDispatcher();

	let updates$ = getLocalCartStream();
	
	function editItemQuantity(item_id: string, quantity: number) {
		updateCartItemQuantity(DEFAULT_MERCHANT_KEY, item_id, quantity);
	}

	const subjQuantity = new Subject<{ id: string; qty: number }>();

	const qStat$ = combineLatest([
		updates$,
		subjQuantity.pipe(
			tap(({ id, qty }) =>
				updateCartItemQuantity(DEFAULT_MERCHANT_KEY, id, qty),
			),
			startWith(null),
		),
	]).pipe(
		map(([latestCart, qtyRequests]) => {
			if (qtyRequests) {
				const item = latestCart?.items?.find(
					(item) => item.catalogue_key === qtyRequests.id,
				);
				if (item) item.quantity = qtyRequests.qty;
			}
			if (latestCart?.items)
				latestCart.items = latestCart.items?.filter((item) => item.quantity);
			return latestCart;
		}),
	);

	function showTip(tip?: Tooltip) {
		// todo: show
	}

	let subscription: Subscription;
	let updateEvents = {} as {
		[key: string]: ObservedValueOf<ReturnType<typeof observeUpdateQtyRequests>>;
	};
	$: {
		subscription?.unsubscribe();
		subscription = observeUpdateQtyRequests().subscribe((update) => {
			let mapped = updateEvents[update.catalogue_key];
			if (!mapped) {
				mapped = { ...update };
			} else {
				mapped.event = update.event;
				mapped.quantity = update.quantity;
			}
			updateEvents[update.catalogue_key] = mapped;
		});
	}

	let clear = (cart: Partial<CartData>) => clearCart(cart);

	async function cont(cart: Partial<CartData> | null) {
		loading = true;
		if (cart?.items?.find(i => !i.in_stock)) {
			showOutOfStockNotice = true;
			return;
		}

		const user = await firstValueFrom(profile$);
		if (user === null) {
			showLogin = true;
			return;
		}
		if (typeof cart?.uuid === "undefined") return;
		// if (cart.scheduled_fulfilment && cart.scheduled_fulfilment.scheduled_fulfilment_type === 'overnight') {
			// we can't allow overnight in Web V2 yet. Get a refresh of the cart and see test if overnight is still there
			updateCartItemQuantity(DEFAULT_MERCHANT_KEY, '', 0, null, true); // cause fresh cart to be fetched
			const fresh = await firstValueFrom(updates$.pipe(skip(1)));
			if (fresh?.scheduled_fulfilment && fresh.scheduled_fulfilment.scheduled_fulfilment_type === 'overnight') {
				// const opensAt = dateTimeStamp(fresh.scheduled_fulfilment.scheduled_fulfilment_ts * 1000);
				showOvernightNotice = true
				loading = false;
				return;
			}
		// }
		try {
			const order = await firstValueFrom(checkout(cart.uuid));
			goto(`/${Vertical.Grocery}/order/${order.id}/checkout`);
		} catch (error) {
			if (typeof error === 'object' && error && 'error_code' in error) {
				if (error.error_code === 'NO_OUT_OF_STOCK_CHECKOUT') {
					showOutOfStockNotice = true;
					return;
				}
			}
			const title = extractErrorMessage(error, 'Unable to check out. Please try again later.');
			toastError(title); // perhaps a local cart message is better?
		}
	}

	let selectedProduct: ProductData;

	let setBackUpItemObj = (
		cartItem: CartItem,
		store_id: string | undefined,
	): ProductData => {
		showBackUpModal = true;
		const newCartItem: ProductData = {
			catalogue_key: cartItem.catalogue_key,
			store_code: store_id ?? "",
			label: cartItem.label,
			description: cartItem.description,
			price_now: cartItem.price,
			url: cartItem.image_url?.filename ?? "",
			in_stock: false,
			low_stock: false,
			stock_status_message: "",
			maximum_order_quantity: cartItem.pnp_quantity,
			is_alcohol: false,
			subaisle_id: cartItem.subaisle_id ?? 0,
			top_pick: false,
			variable_weight: false,
			uom_label: cartItem.unit_of_measure,
			alcohol_delivery_message: "",
			product_type: cartItem.product_type,
			disable_backup: false,
			price_type: "sap",
			lifestyle_endorsements: {
				primary: [],
				secondary: [],
			},
			aisle_id: 0,
			brand: "PnP",
			vitality_flag: false,
			price_was: 0,
			min_sales_weight: cartItem.min_sales_weight,
			max_sales_weight: cartItem.max_sales_weight,
			price_per_kg: cartItem.price_per_kg,
			metadata: "",
			amount_saved: 0,
			has_linked_combos: false,
			has_linked_products: false,
			quantity: cartItem.quantity,
			// techdebt / TODO: I added these props blindly, but I think it's worth looking at this again
			// to understand why we need to construct this object this way.  Everytime the `ProductData`
			// type changes we're going to have to keep coming here to change it accordingly... doesn't
			// feel right.
			product_code: cartItem.product_code,
			badge_groups: JSON.parse(JSON.stringify(cartItem.badge_groups ?? [])), // make a deep copy
			badge_group_collection: [],
			livewell_flag: false,
			request_id_flag: false,
		};
    const { alternative_item: backup } = cartItem;
    if (backup) {
      newCartItem.alternative_item = {
        ...backup,
        price_now: backup.price,
        url: backup.image_url?.filename,
        stock_status_message: "",
      };
    }
		selectedProduct = newCartItem;
		return newCartItem;
	};

	let showBackUpModal: boolean = false;

	function handleBackup(newItem: ProductData) {
		console.log('new item: ', newItem, ' selectedProduct: ', selectedProduct)
		updateCartItemQuantity(
			DEFAULT_MERCHANT_KEY,
			selectedProduct.catalogue_key,
			selectedProduct.quantity,
      newItem ? newItem.catalogue_key : null,
		);
		showBackUpModal = false;
	}
</script>

<Cart {expanded}>
	<!-- example of how to get product update events -->
	<!-- {@const update = updateEvents[item.catalogue_key]}
	|{item.catalogue_key}: {update?.event === 'started' || update?.event === 'started-unchanged' ? 'UPDATING' : 'idle'}| -->

	<svelte:fragment slot="header">
		{@const cart = $qStat$ ?? { items: [] }}
		{@const itemCount = cart?.items?.map((item) => item.quantity).reduce((a, b) => a + b, 0) || 0}
		{#if itemCount}
			<div class="logo"><PnPLogo /></div>
			<div class="fill-space ellipses">
				<h5 class="headline-s bold m-0">Review cart</h5>
				<span class="label-xl medium m-0" data-testid="num-products">
					{itemCount} item{itemCount > 1 ? 's' : ''}
				</span>
				<span class="label-xl book medium medium-importance m-0" data-testid="store-name">
					&middot; {cart.store?.name}
				</span>
			</div>
			<div>
				<button
					on:click={() => dispatch("showCartToggle", false)}
					class="close"
					data-testid="hide-cart"
				>
					<span class="flex icon-close-28"></span>
				</button>
			</div>
		{:else}
			<div class="logo"><PnPLogo /></div>
			<div class="fill-space">
				<h5 class="headline-s bold m-0">Review cart</h5>
				<span class="label-xl medium m-0" data-testid="num-products">0 items</span>
			</div>
			<div>
				<button
					on:click={() => dispatch("showCartToggle", false)}
					class="close"
					data-testid="hide-cart"
				>
					<span class="flex icon-close-28"></span>
				</button>
			</div>
		{/if}
	</svelte:fragment>

	{@const cart = $qStat$}
	{@const hasItems = cart?.items?.length}
	
	{#if hasItems}
		<div class="subheader">
			{#if cart?.delivery_message}
				<p class="label-xl medium fill-space">{cart.delivery_message}</p>
			{:else}
				<p class="label-xl medium fill-space">Delivery TBA</p>				
			{/if}
			<div class="pills m-0">
				<label class="active">
					<input type=radio name="delivery" checked={true}>
					<span>Delivery Only</span>
				</label>
			</div>		
		</div>
	{/if}
	
	{@const itemCount = cart?.items?.map((item) => item.quantity).reduce((a, b) => a + b, 0) || 0}	

	{#if itemCount && cart?.items}
		<CartNotifications cartData={cart} />

		{#each cart?.items as item, index}
			{#if item.quantity > 0}
				<div class="item" data-testid="cart-item">
					<table>
						<tbody>
							<tr>
								<td>
									<LazyBackgroundImage image={item.image_url} alt={item.description}>
										{#if !item.in_stock}
										<div class="sold-out-groc">
											<span class="label-s bold nowrap" data-testid="sold-out-label">Sold out</span>
										</div>
									{:else if item.is_alcohol}
										<span class='age-tag bold'>18+</span>
									{/if}
									</LazyBackgroundImage>
								</td>
								<td class="w-100">
                  {#if item.amount_saved}
                    <span class="label-xs bold promo-pill">{formatCurrency(item.amount_saved)} Saved</span>
                  {/if}
									<h4 class="basis-50 book label-m m-0" data-testid="product-label">
										{#if lowStockChecker(item).hasLowStock}
											<p class="bold label-m m-0">
											<span class="icon-alert-28 font-size-20"></span>
											Running low</p>
										{/if}
										{#if !item.in_stock}
											<span class="strike-through">{item.label}</span>
										{:else}
											{item.label}
										{/if}
									</h4>
								</td>
								<td class="text-right label-xl bold m-0" data-testid="product-price">
                  {#if item.in_stock}
                    <div class="flex-column">
									    <span>{formatCurrency(item.total)}</span>
									    <span class="grey book strike-through">{item.was_total ? formatCurrency(item.was_total) : ''}</span>
                    </div>
                  {/if}
								</td>
							</tr>
							<!-- <tr>
								<td colspan="2" style="background: wheat">table cell</td>
								<td style="background: purple">table cell</td>
							</tr> -->
						</tbody>
					</table>
					<table>
						<tbody>
							<tr>
								<td style="width: 100%;">
									{#if item.alternative_item}
										<div 
											tabindex="0" 
											class="backup"
											role="button" 
											on:click={() => {setBackUpItemObj(item, cart.store?.id)}}
											on:keypress={() => { setBackUpItemObj(item, cart.store?.id)} }
											data-testid="select-backup">
											<LazyImage image={`${PNP_IMG_HOST}/${item.alternative_item.image_url?.filename}`} alt={item.alternative_item.label} id="backup-image" />
											<div data-testid="backup-label">
												<em class="title-backup">Backup</em>
													{item.alternative_item.label}
											</div>
										</div>
									{:else if item.disable_backup}
											<span class="icon-no-backup-20 grey no-backup-allowed" data-testid="backup-disallowed">No backup allowed</span>								
									{:else if item.in_stock}
										<button class="button secondary square inline-flex-row align-center" on:click={() => { setBackUpItemObj(item, cart.store?.id)} } data-testid="select-backup">
											<span class="icon-substitute-20"></span>
											Select a backup</button>
									{/if}
								</td>
								<td class="text-right">
									<Quantity quantity={item.quantity} max={item.maximum_order_quantity} on:update={e => editItemQuantity(item.catalogue_key, e.detail)} />
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			{/if}
		{/each}

	{:else}

		<div class="flex-column height-100 margin">
			{#await import('$lib/ui/shared/icons/CartEmptyGroc.svelte')}
				Please wait...
			{:then CartEmptyGroc}
				<CartEmptyGroc.default />
			{:catch error}
				Failed to load CartEmptyGroc
			{/await}
			<div class="text-center grey gap medium-importance">Your grocery cart is empty.</div>
			<button class="button" on:click={() => dispatch('showCartToggle', false)} data-testid="empty-cart-cta-btn">Shop now</button>
		</div>

	{/if}
		
	{#if itemCount && cart?.items && cart.display_totals?.lines.length}
		<div class="footer" data-testid="price-breakdown">
			{#each cart.display_totals.lines as line}
				<div class="flex-row justify-between fill-space">
					<span class={isFreeDelivery(line.label) ? "text red" : ""}>{line.label}
						{#if line.label === 'Backup Allowance'}
						<span class="tip" style="position: relative;">?
							<ToolTip short message='Because availability of products is always changing, we’ll charge you for the most expensive one of the two. After your item has been purchased, we’ll refund you the difference to your credit wallet.' pos={ToolPosition.BC} />
						</span>
						{/if}
						{#if line.label === 'Wallet credit applied'}
						<span class="tip" style="position: relative;">?
							<ToolTip short message='This credit is automatically deducted from your total when the order is placed. Any used credit can be viewed in your wallet.' pos={ToolPosition.BC} />
						</span>
						{/if}
					</span>
					<span class="bold {isFreeDelivery(line.label) ? 'text red' : ''}">{formatCurrency(line.value)}</span>
				</div>
			{/each}
			<!--<button on:click={() => cont(cart)} disabled={!cart.can_check_out}>{cart.checkout_button_message ?? 'Checkout'}</button>-->
		</div>
	{/if}

	<!--<svelte:fragment slot="footer"></svelte:fragment>-->

	<svelte:fragment slot="footer">
		{@const cart = $qStat$ ?? { items: [] }}
		{@const itemCount = cart?.items?.map((item) => item.quantity).reduce((a, b) => a + b, 0) || 0}
			{#if itemCount && cart?.items}
				<section class="footer">
					<div class="width-100 flex-row small-gap self-end">
						<div class="flex-column">
							<div class="label-s book medium-importance m-0">Total</div>
							<div class="headline-s bold m-0" data-testid="cart-total">
								{#if cart && cart?.items}
									{ cart.items.length > 0 ? formatCurrency(cart?.totals?.total || 0) : "R0"}
									{:else}
									R0
								{/if}
							</div>
						</div>
						{#if cart.can_check_out && (!cart.delivery_status || cart.delivery_status != 'error')}
							<button on:click={() => cont(cart)} 
									class="button fill-space" class:loading 
									disabled={!cart?.items?.length || !cart?.can_check_out}
									data-testid="checkout-btn">
								Continue to checkout
							</button>
							{:else}
								<span class="label-s grey">{cart.above_checkout_button_message || ''}</span>
								<button
									class="button fill-space" 
									disabled={true}
									data-testid="error-checkout-btn">
								{cart.checkout_button_message}
							</button>							
						{/if}
					</div>
					<!--
					{#if cart.delivery_status ===  "error"}
						<span class="text red text-center">{cart.delivery_message}</span>
					{/if}	-->						
			</section>
		{/if}
	</svelte:fragment>

	<!--
<button on:click={() => dispatch('select')}>CLOSE &cross;</button>
<button on:click={() => clear(cart)}>Clear</button>
{#if cart.display_totals?.lines.length}
<section class="footer flex">
<div>
{#each cart.display_totals.lines as line}
<button class={line.type} class:outdated={false} on:click={() => showTip(line.tooltip)} on:keypress>
<button on:click={() => cont(cart)} disabled={!cart.can_check_out}>{cart.checkout_button_message ?? 'Checkout'}</button>
-->
</Cart>

{#if showLogin}
	<Modal on:close={() => (showLogin = false)} id="signin">
		<SignIn on:ok={() => cont($qStat$)} />
	</Modal>
{/if}

{#if showBackUpModal}
	<Modal on:close={() => (showBackUpModal = false)} id="backup" size='large'>
		<BackupSelect
			product={selectedProduct}
			on:backup={(e) => handleBackup(e.detail)}
		></BackupSelect>
	</Modal>
{/if}

{#if showOvernightNotice}
	<Modal on:close={() => (showOvernightNotice = false)} id="overnight">
		<div class="m-auto text-center">
			<h3 class="padding-top-16 headline-s bold m-0">The store is closed</h3>
			<p class="body-m book">Please checkout when the store re-opens.</p>
			<button class="button wide" on:click={() => showOvernightNotice = false}>OK, got it</button>
		</div>
	</Modal>
{/if}

{#if showOutOfStockNotice}
	<Modal size="medium" on:close={() => showOutOfStockNotice = false} id="out_of_stock">
		<div class="m-auto text-center">
			<h3 class="padding-top-16 headline-s bold m-0">Items out of stock</h3>
			<p class="body-m book">Some of the items in your cart are currently out of stock. Please remove these products in order to check out.</p>
			<button class="button wide" on:click={() => showOutOfStockNotice = false}>OK, got it</button>
		</div>
	</Modal>
{/if}

<style lang="scss">
	@use "margin";
	@use "color";
	.gap {
		@include margin.medium;
	}

	.margin {
		margin: 0 16px;
	}	

	.no-backup-allowed {
		font-size: 15px !important;
		&:before {
			margin-right: 9px;
			top: 1px;
			position: relative;			
		}
	}
	.tip {
		position: relative;
		cursor: pointer;
		border: 1px solid color.$on-surface;
    border-radius: 50%;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    min-width: 12px;
    height: 12px;
    font-size: 0.8rem;
    margin-left: 0.3rem;
	}
</style>
